import { parseAsString, useQueryState } from "nuqs";
import { useCallback, useEffect, useRef } from "react";

import { FullScreenLoader } from "../components/ui/loaders/full-screen-loader.js";
import {
  clearRememberedAdminConsentSearch,
  getAdminConsentErrorRedirectPath,
  getAdminConsentSuccessRedirectPath,
  getRememberedAdminConsentSearch,
  hasResponseFromMicrosoft,
  isAdminConsentSuccess,
  WAIT_FOR_RESPONSE_FROM_MICROSOFT_MS,
} from "../features/auth/admin-consent-logic.js";
import type { AdminConsentFlow } from "../features/auth/admin-consent-logic.js";
import {
  notifyTeamsAuthFailure,
  notifyTeamsAuthSuccess,
} from "../features/auth/teams-auth-popup-callback.js";
import { recordErrorInSentry } from "../utils/init-sentry.js";
import { caseInsensitiveBoolean } from "../utils/nuqs-parsers.js";

const assignLocation = (url: string) => {
  if (typeof window === "undefined") {
    return;
  }

  window.location.assign(url);
};

const toError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

export const AdminConsentEndPage = ({ flow }: { flow: AdminConsentFlow }) => {
  const timer = useRef<null | ReturnType<typeof setTimeout>>(null);
  const hasHandledMicrosoftResponse = useRef(false);

  // Handle response from Microsoft
  // When navigating back to this page, we expect following query params to be set by microsoft:
  // - admin_consent: true/false
  // - error: code of the error
  // - error_description: description of the error
  // - tenant: tenant id of the tenant that granted the permissions
  const [hasPermissions] = useQueryState(
    "admin_consent",
    caseInsensitiveBoolean
  );
  const [permissionsError] = useQueryState("error", parseAsString);
  const [permissionsGrantedForTenantId] = useQueryState(
    "tenant",
    parseAsString
  );
  const [grantedScope] = useQueryState("scope", parseAsString);
  const [returnedState] = useQueryState("state", parseAsString);

  const hasMicrosoftResponse = hasResponseFromMicrosoft({
    hasPermissions,
    permissionsError,
    permissionsGrantedForTenantId,
  });
  const isSuccess = isAdminConsentSuccess({
    hasPermissions,
    permissionsError,
    permissionsGrantedForTenantId,
  });
  const failureReason = "Admin consent was not granted";
  const rememberedSearch = getRememberedAdminConsentSearch(flow);
  const successRedirectUrl = getAdminConsentSuccessRedirectPath(
    flow,
    rememberedSearch
  );
  const errorRedirectUrl = getAdminConsentErrorRedirectPath(
    flow,
    rememberedSearch,
    permissionsError || hasPermissions !== false ? "error" : "denied"
  );

  const assignConsentRedirect = useCallback(
    (url: string) => {
      clearRememberedAdminConsentSearch(flow);
      assignLocation(url);
    },
    [flow]
  );

  const redirectToEnsureTenantPageWithError = useCallback(() => {
    if (hasMicrosoftResponse) {
      return;
    }

    recordErrorInSentry(
      new Error(
        "No response from Microsoft received in time in AdminConsentEndPage"
      ),
      {
        extra: {
          currentUrl:
            typeof window === "undefined" ? null : window.location.href,
          flow,
        },
      }
    );

    const notifyPopup = async () => {
      try {
        const hasNotifiedTeamsPopup = await notifyTeamsAuthFailure("error");
        if (hasNotifiedTeamsPopup) {
          return;
        }

        assignConsentRedirect(
          getAdminConsentErrorRedirectPath(flow, rememberedSearch, "error")
        );
      } catch (error: unknown) {
        recordErrorInSentry(toError(error));
        assignConsentRedirect(errorRedirectUrl);
      }
    };

    notifyPopup();
  }, [
    assignConsentRedirect,
    errorRedirectUrl,
    flow,
    hasMicrosoftResponse,
    rememberedSearch,
  ]);

  // Redirect back to the EnsureTenantPage with error set if we don't get a response from Microsoft after the timeout.
  useEffect(() => {
    if (hasMicrosoftResponse || timer.current) {
      return;
    }

    timer.current = setTimeout(
      redirectToEnsureTenantPageWithError,
      WAIT_FOR_RESPONSE_FROM_MICROSOFT_MS
    );

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [hasMicrosoftResponse, redirectToEnsureTenantPageWithError]);

  useEffect(() => {
    if (!hasMicrosoftResponse || hasHandledMicrosoftResponse.current) {
      return;
    }

    hasHandledMicrosoftResponse.current = true;

    const handleMicrosoftResponse = async () => {
      if (isSuccess) {
        const hasNotifiedTeamsPopup = await notifyTeamsAuthSuccess();
        if (!hasNotifiedTeamsPopup) {
          assignConsentRedirect(successRedirectUrl);
        }
        return;
      }

      recordErrorInSentry(new Error(failureReason), {
        extra: {
          currentUrl:
            typeof window === "undefined" ? null : window.location.href,
          error: permissionsError,
          flow,
          grantedScope,
          hasPermissions,
          returnedState,
          tenant: permissionsGrantedForTenantId,
        },
      });

      const hasNotifiedTeamsPopup = await notifyTeamsAuthFailure(
        permissionsError || hasPermissions !== false ? "error" : "denied"
      );
      if (!hasNotifiedTeamsPopup) {
        assignConsentRedirect(errorRedirectUrl);
      }
    };

    const handleResponse = async () => {
      try {
        await handleMicrosoftResponse();
      } catch (error: unknown) {
        recordErrorInSentry(toError(error));

        if (isSuccess) {
          assignConsentRedirect(successRedirectUrl);
          return;
        }

        assignConsentRedirect(errorRedirectUrl);
      }
    };

    handleResponse();
  }, [
    assignConsentRedirect,
    errorRedirectUrl,
    failureReason,
    flow,
    grantedScope,
    hasPermissions,
    hasMicrosoftResponse,
    isSuccess,
    permissionsError,
    permissionsGrantedForTenantId,
    returnedState,
    successRedirectUrl,
  ]);

  return <FullScreenLoader message="Redirecting..." />;
};
